import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Link, Stack, Typography, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
// form
import { useForm } from 'react-hook-form';
import FormProvider, { RHFMultiCheckboxCustomise,RHFSelect } from '../../components/hook-form';
// auth·
import { useAuthContext } from '../../auth/useAuthContext';
// components
import { useSnackbar } from '../../components/snackbar';
import useResponsive from '../../hooks/useResponsive';
import Iconify from '../../components/iconify';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { getUserProfile } from '../../redux/slices/profile';
import { addProgramme, viewProgramme } from '../../redux/slices/programme';

export default function FormPage() {
  const isMdUp = useResponsive('up', 'md');
  const theme = useTheme();
  const PRIMARY_MAIN = theme.palette.primary.main;
  const { user } = useAuthContext();
  const { userProfile } = useSelector((state) => state.profile);
  const { programmeList } = useSelector((state) => state.programme);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openResultDialog, setOpenResultDialog] = useState(false);
  const [totalPoints, setTotalPoints] = useState(0);
  const [submittedProgrammes, setSubmittedProgrammes] = useState([]);
  const [submittedYear, setSubmittedYear] = useState("");
  
  const d = new Date();
  const year = d.getFullYear();
  const [Year, setYear] = useState([]);

  useEffect(() => {
    if (user && user.UserID) {
      dispatch(getUserProfile('UserProfile', user?.UserID, 0, 999, 1, user?.UserID));
      dispatch(viewProgramme());
    } else {
      navigate('/login')
    }
  }, [dispatch, navigate, user]);

  useEffect(() => {
    if (!userProfile?.FirstName || !userProfile?.LastName || !userProfile?.YearIntake) {
      enqueueSnackbar('Please fill in first name, last name and year of intake', { variant: 'error' });
      navigate('/account');
    }
    
    
    const selectionYear=[]
    /* eslint-disable no-plusplus */
    if(year - userProfile.YearIntake > 0)
    {
      for (let i = userProfile.YearIntake; i <=  year - userProfile.YearIntake; i++) {
        if(userProfile.YearIntake<=userProfile.YearIntake+5){
          selectionYear.push(i)
        }
      }
    }else{
      selectionYear.push(userProfile.YearIntake)
    }
    
    setYear(selectionYear)
  }, [userProfile, year, navigate, enqueueSnackbar]);

  const defaultValues = {
    programmes: [],
    Year: userProfile.YearIntake || year,
  };

  const methods = useForm({
    defaultValues,
  });

  const {
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting },
  } = methods;

  const watchedProgrammes = watch("programmes");

  const calculateTotalPoints = () =>
    (Array.isArray(watchedProgrammes) ? watchedProgrammes : []).reduce(
      (total, programmeId) => total + (programmeList.find((item) => item.ProgrammeID === programmeId)?.Mark || 0),
      0
    );

  const onSubmit = async (data) => {
    console.log(data)
    const selectedProgrammes = data.programmes;
    const total = calculateTotalPoints();
    setTotalPoints(total);
    setSubmittedProgrammes(selectedProgrammes);
    setSubmittedYear(data.Year);
    setOpenConfirmDialog(true);
  };

  const handleConfirmSubmit = () => {
    submittedProgrammes.map((programmeId) => {
      const programme = programmeList.find((item) => item.ProgrammeID === programmeId);
      if (programme) {
        dispatch(addProgramme(user?.UserID, programmeId, programme.Mark, submittedYear));
      }
      return programme;
    });

    setOpenConfirmDialog(false);
    setOpenResultDialog(true);
  };


  const handleConfirmClose = () => {
    setOpenConfirmDialog(false);
  };

  const handleResultClose = () => {
    setOpenResultDialog(false);
    reset(defaultValues);
  };

  const handleClear = () => {
    reset(defaultValues);
  };


  return (
    <>
      <Helmet>
        <title>Certified MAGScholar© Programme | MAGScholar</title>
      </Helmet>

      <Box sx={{ position: 'relative', p: 0, m: { xs: 1, sm: 1, md: 5, lg: 0 }, mt: { xs: 1, sm: 1, md: 10, lg: 10 } }}>
        {isMdUp &&
          <Box
            sx={{
              ml: -5,
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              zIndex: -1,
              width: '90%',
              height: '100%',
              backgroundColor: 'rgb(243, 108, 79, 0.1)',
              borderTopRightRadius: '500px',
              borderBottomRightRadius: '500px',
            }}
          />
        }

        <Container>
          <Box sx={{ display: 'flex', flexDirection: isMdUp ? 'row' : 'column', flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 20%', pr: 3 }}>
              <Typography variant="h4" lineHeight={1.2} mb={1}>
                Certified MAGScholar© Programme
              </Typography>

              <Typography variant="body2" color="text.secondary" textAlign="justify" paragraph>
                Select the programmes. Each has a specific point value. You can choose multiple programmes, and the total points will be calculated automatically. <br /><br />
                To graduate with as a Certified MAGScholar© you need to achieve <strong>420 points</strong> within <strong>FIVE years</strong> fulfilling specific duties at least once.
              </Typography>

              {userProfile !== null &&
                <>
                  <Typography variant="caption">Your Name</Typography>
                  <Typography variant="body1" fontWeight={600}>{userProfile?.UserFullName}</Typography>
                </>
              }
            </Box>

            <Box sx={{ flex: '1 1 50%' }}>
              <Box sx={{ backgroundColor: PRIMARY_MAIN, borderRadius: isMdUp ? 5 : 3, p: isMdUp ? 5 : 2, my: isMdUp ? -10 : 3 }}>
                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                  {Array.isArray(programmeList) && (
                    <RHFMultiCheckboxCustomise
                      name="programmes"
                      options={programmeList.map(prog => ({ id: prog.ProgrammeID, label: prog.ProgrammeName, mark: prog.Mark }))}
                      optionLabel="label"
                      optionValue="id"
                      value={watchedProgrammes}
                    />
                  )}

                  <RHFSelect native name="Year" label="Year of Intake">
                    {Year.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </RHFSelect>

                  <Stack direction="row" alignItems="center" justifyContent="space-between" mt={2}>
                    <Stack direction="column" color="#fff">
                      <Typography variant="body1">Total Points</Typography>
                      <Typography variant="h6">{calculateTotalPoints()}</Typography>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Link onClick={handleClear} underline="hover" sx={{ cursor: 'pointer', fontSize: 13, color: '#fff' }}>
                        Clear
                      </Link>

                      <LoadingButton type="submit" variant="contained" loading={isSubmitting} sx={{ borderRadius: 50, backgroundColor: 'rgb(255, 255, 255, 0.3)' }}>
                        Submit
                      </LoadingButton>
                    </Stack>
                  </Stack>
                </FormProvider>
              </Box>
            </Box>
          </Box>
        </Container>

        <Dialog open={openConfirmDialog} onClose={handleConfirmClose}>
          <DialogTitle>Confirmation</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Your total points for the selected programs are <strong>{totalPoints}</strong>. Would you like to submit?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmClose} color="primary">
              No
            </Button>
            <LoadingButton onClick={handleConfirmSubmit} color="primary">
              Yes
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Dialog open={openResultDialog} onClose={handleResultClose}>
          <DialogTitle>
            <Stack direction="column" alignItems="center" justifyContent="center">
              <Iconify icon="line-md:check-all" width={80} color="green" />
              <Typography variant='body1' fontWeight={600}>Submitted to Admin!</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Your total points for the selected programmes are <strong>{totalPoints}</strong>.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleResultClose} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
